/**
 * Canvas 2D: прежняя композиция колец, золотых спиралей и центрального огня.
 * Свет и детали ограничены pixel budget; все фазы идут по времени, не по FPS.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import {
  initialParticleQuality,
  lowerParticleQuality,
  PARTICLE_QUALITY,
  particleResolution,
  shouldReduceParticleQuality,
} from './lib/particleQuality';
import { mulberry32 } from './lib/prng';

interface Rgb {
  r: number;
  g: number;
  b: number;
}
interface Palette {
  gold: Rgb;
  bright: Rgb;
  pale: Rgb;
  teal: Rgb;
  deep: Rgb;
  light: number;
}
interface Star {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  phase: number;
  speed: number;
  teal: boolean;
}
interface CoilSample {
  distance: number;
  offsetX: number;
  envelope: number;
  phase: number;
  rx: number;
  ry: number;
  alpha: number;
  glint: boolean;
}
export interface ParticleFieldHandle {
  setActive(active: boolean): void;
}
interface ParticleFieldProps {
  className?: string;
}

const TAU = Math.PI * 2;
const GLOW_SIZE = 192;
const THEME_DURATION_MS = 450;
const FALLBACK: Rgb = { r: 128, g: 128, b: 128 };

function readColor(style: CSSStyleDeclaration, name: string): Rgb {
  const raw = style.getPropertyValue(name).trim();
  const hex = raw.startsWith('#') ? raw.slice(1) : '';
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return FALLBACK;
  const value = Number.parseInt(full, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}
function readPalette(): Palette {
  const root = document.documentElement;
  const style = getComputedStyle(root);
  return {
    gold: readColor(style, '--sg-gold-500'),
    bright: readColor(style, '--sg-gold-300'),
    pale: readColor(style, '--sg-gold-100'),
    teal: readColor(style, '--sg-teal-300'),
    deep: readColor(style, '--sg-teal-500'),
    light: root.dataset.theme === 'light' ? 1 : 0,
  };
}
function rgba(color: Rgb, alpha = 1): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}
function mixColor(from: Rgb, to: Rgb, blend: number): Rgb {
  return {
    r: Math.round(from.r + (to.r - from.r) * blend),
    g: Math.round(from.g + (to.g - from.g) * blend),
    b: Math.round(from.b + (to.b - from.b) * blend),
  };
}
/** A bounded soft wave, without emitters or trails accumulating in memory. */
function lightAt(distance: number, position: number, width: number): number {
  const value = Math.max(0, 1 - Math.abs(distance - position) / width);
  return value * value * (3 - 2 * value);
}
/** Two small cached glow sprites, never an extra full-screen canvas. */
function paintGlow(atlas: HTMLCanvasElement, palette: Palette): void {
  const context = atlas.getContext('2d');
  if (!context) return;
  context.clearRect(0, 0, atlas.width, atlas.height);
  const radius = GLOW_SIZE / 2;
  const core = context.createRadialGradient(radius, radius, 0, radius, radius, radius);
  const coreStrength = 1 - palette.light * 0.65;
  core.addColorStop(0, rgba(palette.deep, 0.12 * coreStrength));
  core.addColorStop(0.35, rgba(palette.deep, 0.04 * coreStrength));
  core.addColorStop(1, rgba(palette.deep, 0));
  context.fillStyle = core;
  context.fillRect(0, 0, GLOW_SIZE, GLOW_SIZE);
  const center = GLOW_SIZE + radius;
  const spark = context.createRadialGradient(center, radius, 0, center, radius, radius);
  const sparkStrength = 1 - palette.light * 0.2;
  spark.addColorStop(0, rgba(palette.pale, 0.95 * sparkStrength));
  spark.addColorStop(0.12, rgba(palette.bright, 0.6 * sparkStrength));
  spark.addColorStop(0.45, rgba(palette.gold, 0.18 * sparkStrength));
  spark.addColorStop(1, rgba(palette.gold, 0));
  context.fillStyle = spark;
  context.fillRect(GLOW_SIZE, 0, GLOW_SIZE, GLOW_SIZE);
}

export const ParticleField = forwardRef<ParticleFieldHandle, ParticleFieldProps>(
  ({ className }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const activeRef = useRef(true);
    const syncRef = useRef<() => void>(() => {});
    useImperativeHandle(ref, () => ({
      setActive(active: boolean) {
        activeRef.current = active;
        syncRef.current();
      },
    }));

    useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d', { alpha: true });
      if (!canvas || !ctx) return;
      const hints = navigator as Navigator & {
        deviceMemory?: number;
        connection?: { saveData?: boolean };
      };
      let quality = initialParticleQuality({
        deviceMemory: hints.deviceMemory,
        hardwareConcurrency: hints.hardwareConcurrency,
        saveData: hints.connection?.saveData,
      });
      let settings = PARTICLE_QUALITY[quality];
      const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      let reduceMotion = motionQuery.matches;
      // Some Telegram Desktop WebViews report hidden while actually on screen.
      const desktopTelegram = ['tdesktop', 'macos', 'unigram'].includes(
        window.Telegram?.WebApp?.platform ?? '',
      );
      let pageActive = true;
      let visibilityPaused = false;
      let contextLost = false;
      let raf = 0;
      let phase = 0;
      let lastTs = 0;
      let lastPaintTs = 0;
      let nextPaintTs = 0;
      let resizePending = false;
      let width = 0;
      let height = 0;
      let nativeDpr = 0;
      let centerX = 0;
      let horizonY = 0;
      let coreRadius = 0;
      let sparkRadius = 0;
      let ringCount = 0;
      let stars: Star[] = [];
      let coils: CoilSample[] = [];
      let sampleStart = 0;
      let sampledFrames = 0;
      let renderTimeMs = 0;
      let expensiveFrames = 0;
      let slowFrames = 0;
      let palette = readPalette();
      let fromPalette = palette;
      let targetPalette = palette;
      let themeStarted = 0;
      let themeBlend = 1;
      let gold = '';
      let bright = '';
      let pale = '';
      let teal = '';
      const fromGlow = document.createElement('canvas');
      const targetGlow = document.createElement('canvas');
      fromGlow.width = targetGlow.width = GLOW_SIZE * 2;
      fromGlow.height = targetGlow.height = GLOW_SIZE;
      paintGlow(fromGlow, palette);
      paintGlow(targetGlow, palette);

      function updateColors(): void {
        gold = rgba(palette.gold);
        // The same gold palette, with a little more definition on the cream theme.
        bright = rgba(mixColor(palette.bright, palette.gold, palette.light * 0.28));
        pale = rgba(palette.pale);
        teal = rgba(palette.teal);
      }
      updateColors();
      function resetSamples(now: number): void {
        sampleStart = now + 750;
        sampledFrames = renderTimeMs = expensiveFrames = slowFrames = 0;
        lastPaintTs = 0;
      }
      function layout(force = false): void {
        const rect = canvas!.getBoundingClientRect();
        const nextWidth = Math.max(1, Math.round(rect.width));
        const nextHeight = Math.max(1, Math.round(rect.height));
        const nextDpr = window.devicePixelRatio || 1;
        if (!force && width === nextWidth && height === nextHeight && nativeDpr === nextDpr) return;
        width = nextWidth;
        height = nextHeight;
        nativeDpr = nextDpr;
        const resolution = particleResolution(width, height, nativeDpr, quality);
        if (canvas!.width !== resolution.width) canvas!.width = resolution.width;
        if (canvas!.height !== resolution.height) canvas!.height = resolution.height;
        ctx!.setTransform(resolution.scaleX, 0, 0, resolution.scaleY, 0, 0);
        centerX = width / 2;
        horizonY = height * 0.46;
        coreRadius = Math.max(width, height) * 0.55;
        sparkRadius = Math.max(46, width * 0.09);
        // Keep corridor spacing and speed identical when quality changes.
        ringCount = Math.round(Math.min(30, Math.max(14, width / 46)));
        const count = Math.round(
          Math.min(320, Math.max(70, (width * height) / 5200)) * settings.density,
        );
        const random = mulberry32(0x5ea1);
        stars = [];
        for (let i = 0; i < count; i += 1) {
          const x = random() * width;
          const y = random() * height * 0.78;
          if (Math.hypot((x - centerX) / width, (y - horizonY) / height) < 0.1 && random() < 0.8)
            continue;
          stars.push({
            x,
            y,
            radius: 0.4 + random() * 1.1,
            alpha: 0.12 + random() * 0.55,
            phase: random() * TAU,
            speed: 0.25 + random() * 0.7,
            teal: random() < 0.28,
          });
        }
        const samples = Math.round(Math.min(110, Math.max(38, width / 13)) * settings.density);
        coils = [];
        for (let k = 0; k <= samples; k += 1) {
          const s = k / samples;
          const ry = height * 0.085 * Math.pow(1 - s, 1.55) + 0.6;
          coils.push({
            distance: 1 - s,
            offsetX: width * 0.54 * Math.pow(1 - s, 1.8),
            envelope: height * 0.17 * Math.pow(1 - s, 1.25),
            phase: s * TAU * 1.15,
            rx: ry * 0.38 + 0.4,
            ry,
            alpha: 0.5 * Math.pow(1 - s, 0.55) + 0.06,
            glint: k % (quality === 'low' ? 6 : 4) === 0,
          });
        }
        resetSamples(performance.now());
      }
      function updateTheme(now: number): void {
        if (themeBlend === 1) return;
        const progress = reduceMotion ? 1 : Math.min(1, (now - themeStarted) / THEME_DURATION_MS);
        themeBlend = progress * progress * (3 - 2 * progress);
        if (progress === 1) palette = targetPalette;
        else {
          palette = {
            gold: mixColor(fromPalette.gold, targetPalette.gold, themeBlend),
            bright: mixColor(fromPalette.bright, targetPalette.bright, themeBlend),
            pale: mixColor(fromPalette.pale, targetPalette.pale, themeBlend),
            teal: mixColor(fromPalette.teal, targetPalette.teal, themeBlend),
            deep: mixColor(fromPalette.deep, targetPalette.deep, themeBlend),
            light: fromPalette.light + (targetPalette.light - fromPalette.light) * themeBlend,
          };
        }
        updateColors();
      }
      function drawGlow(spark: boolean, alpha: number): void {
        const radius = spark ? sparkRadius : coreRadius;
        const sourceX = spark ? GLOW_SIZE : 0;
        if (themeBlend < 1) {
          ctx!.globalAlpha = alpha * (1 - themeBlend);
          ctx!.drawImage(
            fromGlow,
            sourceX,
            0,
            GLOW_SIZE,
            GLOW_SIZE,
            centerX - radius,
            horizonY - radius,
            radius * 2,
            radius * 2,
          );
        }
        ctx!.globalAlpha = alpha * themeBlend;
        ctx!.drawImage(
          targetGlow,
          sourceX,
          0,
          GLOW_SIZE,
          GLOW_SIZE,
          centerX - radius,
          horizonY - radius,
          radius * 2,
          radius * 2,
        );
        ctx!.globalAlpha = 1;
      }
      function drawStars(t: number): void {
        for (const star of stars) {
          const twinkle = reduceMotion ? 1 : 0.65 + 0.35 * Math.sin(t * star.speed + star.phase);
          ctx!.globalAlpha = star.alpha * twinkle;
          ctx!.fillStyle = star.teal ? teal : bright;
          ctx!.beginPath();
          ctx!.arc(star.x, star.y, star.radius, 0, TAU);
          ctx!.fill();
        }
        ctx!.globalAlpha = 1;
      }
      /** The original complete bright ellipse, with a restrained halo and sparse glint. */
      function drawMetalRing(
        x: number,
        y: number,
        rx: number,
        ry: number,
        tilt: number,
        alpha: number,
        lineWidth: number,
        haloWidth: number,
        glint: number,
      ): void {
        if (settings.halo) {
          ctx!.globalAlpha = alpha * 0.22 * (1 - palette.light * 0.3);
          ctx!.strokeStyle = gold;
          ctx!.lineWidth = haloWidth;
          ctx!.beginPath();
          ctx!.ellipse(x, y, rx, ry, tilt, 0, TAU);
          ctx!.stroke();
        }
        ctx!.strokeStyle = bright;
        ctx!.lineWidth = lineWidth;
        ctx!.globalAlpha = Math.min(0.9, alpha * (1.15 + palette.light * 0.45));
        ctx!.beginPath();
        ctx!.ellipse(x, y, rx, ry, tilt, 0, TAU);
        ctx!.stroke();
        if (glint > 0.01 && rx > 2) {
          ctx!.globalAlpha = Math.min(0.7, alpha * glint);
          ctx!.strokeStyle = pale;
          ctx!.beginPath();
          ctx!.ellipse(x, y, rx, ry, tilt, 0.15 * Math.PI, 0.48 * Math.PI);
          ctx!.stroke();
        }
      }
      function drawCorridor(t: number, wave: number, shimmer: number): void {
        const drift = reduceMotion ? 0 : (t * 0.06) % 1;
        for (let i = 0; i < ringCount; i += 1) {
          const base = (i + drift) / ringCount;
          const d = Math.pow(base, 2.3);
          const rx = width * 0.47 * d;
          if (rx < 0.6) continue;
          const light = lightAt(d, wave, 0.2);
          const alpha = 0.5 * Math.min(1, base * 6) * (1 - Math.pow(base, 2.2) * 0.85);
          drawMetalRing(
            centerX,
            horizonY + height * 0.44 * d,
            rx,
            rx * 0.24,
            0,
            alpha * (1 + light * 0.14),
            Math.max(0.5, 0.4 + d * 0.9),
            1 + d * 2.4,
            i % 3 === 0 ? lightAt(d, shimmer, 0.18) * 0.65 : 0,
          );
        }
        ctx!.globalAlpha = 1;
      }
      function drawCoils(t: number, wave: number, shimmer: number): void {
        const wavePhase = reduceMotion ? 0.9 : t * 0.35;
        for (const sample of coils) {
          const angle = sample.phase + wavePhase;
          const y = horizonY + Math.sin(angle) * sample.envelope;
          const tilt = Math.atan(Math.cos(angle) * sample.envelope * 0.02);
          const light = lightAt(sample.distance, wave, 0.2);
          const glint = sample.glint ? lightAt(sample.distance, shimmer, 0.18) * 0.7 : 0;
          for (let side = -1; side <= 1; side += 2) {
            drawMetalRing(
              centerX + side * sample.offsetX,
              y,
              sample.rx,
              sample.ry,
              tilt * side,
              sample.alpha * (1 + light * 0.14),
              0.7,
              2.4,
              glint,
            );
          }
        }
        ctx!.globalAlpha = 1;
      }
      function drawSpark(t: number, wave: number): void {
        const breathe = reduceMotion ? 1 : 0.85 + 0.15 * Math.sin(t * 0.3);
        const flicker = reduceMotion ? 1 : 0.95 + 0.05 * Math.sin(t * 2.5 + Math.sin(t * 1.3));
        const pulse = breathe * flicker;
        const emission = lightAt(0, wave, 0.2) * 0.04;
        const timeGrowth = Math.min(0.3, t * 0.001);
        drawGlow(true, Math.min(1, (0.88 + 0.12 * breathe + emission) * (1 + timeGrowth * 0.5)));
        ctx!.globalAlpha = Math.min(1, (0.95 + Math.min(0.2, t * 0.0003)) * pulse + emission);
        ctx!.fillStyle = pale;
        ctx!.beginPath();
        ctx!.arc(centerX, horizonY, Math.max(2.2, width * 0.0035) * pulse, 0, TAU);
        ctx!.fill();
        const sparkCount = 3 + Math.floor(Math.min(2, t * 0.003));
        for (let i = 0; i < sparkCount; i += 1) {
          const angle = (t * 0.5 + (i * TAU) / sparkCount) % TAU;
          const dist = 8 + 6 * Math.sin(t * 1.2 + i);
          ctx!.globalAlpha = 0.6 * pulse;
          ctx!.fillStyle = bright;
          ctx!.beginPath();
          ctx!.arc(
            centerX + Math.cos(angle) * dist,
            horizonY + Math.sin(angle) * dist,
            0.8 + Math.sin(t * 3 + i) * 0.4,
            0,
            TAU,
          );
          ctx!.fill();
        }
        ctx!.globalAlpha = 1;
      }
      function draw(now: number): void {
        updateTheme(now);
        const t = reduceMotion ? 0.9 : phase;
        const wave = reduceMotion ? -2 : (t % 16) / 4;
        const shimmer = reduceMotion ? -2 : ((t + 5) % 19) / 6;
        ctx!.clearRect(0, 0, width, height);
        drawGlow(false, 1);
        drawStars(t);
        drawCorridor(t, wave, shimmer);
        drawCoils(t, wave, shimmer);
        drawSpark(t, wave);
      }
      function frame(ts: number): void {
        if (resizePending) {
          resizePending = false;
          layout();
        }
        const dt = lastTs === 0 ? 0 : Math.min((ts - lastTs) / 1000, 0.1);
        lastTs = ts;
        phase += dt;
        if (ts + 0.5 >= nextPaintTs) {
          const interval = 1000 / settings.fps;
          const started = performance.now();
          draw(ts);
          const cost = performance.now() - started;
          if (ts >= sampleStart) {
            sampledFrames += 1;
            renderTimeMs += Math.min(cost, interval * 2);
            if (cost > interval * 0.5) expensiveFrames += 1;
            if (lastPaintTs !== 0 && ts - lastPaintTs > interval * 1.8) slowFrames += 1;
            const elapsedMs = ts - sampleStart;
            // Very slow devices may need longer than one window to collect 20 frames.
            if (elapsedMs >= 2500 && sampledFrames >= 20) {
              if (
                shouldReduceParticleQuality(quality, {
                  frames: sampledFrames,
                  elapsedMs,
                  renderTimeMs,
                  expensiveFrames,
                  slowFrames,
                })
              ) {
                quality = lowerParticleQuality(quality);
                settings = PARTICLE_QUALITY[quality];
                layout(true);
              } else resetSamples(ts);
            }
          }
          lastPaintTs = ts;
          // Remainder keeps 45fps smooth on 60/90/120Hz displays, without catch-up loops.
          nextPaintTs = ts + interval - Math.min(interval, Math.max(0, ts - nextPaintTs));
        }
        raf = window.requestAnimationFrame(frame);
      }
      function stop(): void {
        if (raf !== 0) window.cancelAnimationFrame(raf);
        raf = 0;
        lastTs = 0;
      }
      function sync(): void {
        if (!activeRef.current || !pageActive || visibilityPaused || contextLost || reduceMotion) {
          stop();
          return;
        }
        if (raf !== 0) return;
        lastTs = nextPaintTs = 0;
        resetSamples(performance.now());
        raf = window.requestAnimationFrame(frame);
      }
      syncRef.current = sync;
      function onResize(): void {
        if (raf !== 0) resizePending = true;
        else {
          layout();
          if (!contextLost) draw(performance.now());
        }
      }
      function onTheme(): void {
        const now = performance.now();
        updateTheme(now);
        fromPalette = palette;
        targetPalette = readPalette();
        paintGlow(fromGlow, fromPalette);
        paintGlow(targetGlow, targetPalette);
        themeStarted = now;
        themeBlend = 0;
        resetSamples(now);
        if (raf === 0) {
          palette = targetPalette;
          themeBlend = 1;
          updateColors();
          if (!contextLost) draw(now);
        }
      }
      function onMotion(): void {
        reduceMotion = motionQuery.matches;
        if (reduceMotion) {
          stop();
          updateTheme(performance.now());
          draw(performance.now());
        }
        sync();
      }
      function onVisibility(): void {
        // Initial hidden is deliberately not a startup gate for embedded WebViews.
        visibilityPaused =
          !desktopTelegram && document.visibilityState === 'hidden' && !document.hasFocus();
        if (!visibilityPaused) pageActive = true;
        sync();
      }
      function onPageHide(): void {
        pageActive = false;
        sync();
      }
      function onResume(): void {
        pageActive = true;
        visibilityPaused = false;
        sync();
      }
      function onContextLost(event: Event): void {
        event.preventDefault();
        contextLost = true;
        sync();
      }
      function onContextRestored(): void {
        contextLost = false;
        layout(true);
        draw(performance.now());
        sync();
      }
      const resizeObserver = new ResizeObserver(onResize);
      const themeObserver = new MutationObserver(onTheme);
      resizeObserver.observe(canvas);
      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
      });
      motionQuery.addEventListener('change', onMotion);
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pagehide', onPageHide);
      window.addEventListener('pageshow', onResume);
      window.addEventListener('focus', onResume);
      window.addEventListener('resize', onResize);
      canvas.addEventListener('contextlost', onContextLost);
      canvas.addEventListener('contextrestored', onContextRestored);
      layout();
      draw(performance.now());
      sync();
      return () => {
        stop();
        resizeObserver.disconnect();
        themeObserver.disconnect();
        motionQuery.removeEventListener('change', onMotion);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onResume);
        window.removeEventListener('focus', onResume);
        window.removeEventListener('resize', onResize);
        canvas.removeEventListener('contextlost', onContextLost);
        canvas.removeEventListener('contextrestored', onContextRestored);
        syncRef.current = () => {};
        stars = [];
        coils = [];
        // Release backing stores on route changes, including StrictMode remounts.
        fromGlow.width = fromGlow.height = targetGlow.width = targetGlow.height = 1;
        canvas.width = canvas.height = 1;
      };
    }, []);
    return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
  },
);
ParticleField.displayName = 'ParticleField';
